import { useEffect, useEffectEvent } from 'react'
import omit from 'lodash/omit'
import { pollJobStatusUntilDone } from 'pages/AICreation/AICreationFlow/AICreationFlow.utils'
import { generateAIImage } from 'pages/AICreation/AICreationFlow/api'
import { getAmplitudeDeviceId } from 'services/Analytics/platforms/amplitude'
import { useExperimentManager } from 'services/ExperimentManager/ExperimentManager'
import { getBookPhotosUids } from 'services/PhotoBook/photoBookUtils'
import { useCartStore } from 'stores/cartStore/CartStoreProvider'
import {
  usePhotoBankStore,
  usePhotoBooksStore,
  useTilesStore,
} from 'stores/RootStoreProvider'
import { useMobxSelector } from 'utils/mobx/useMobxSelector'
import type { CartUpsellGeneration } from '../../cartUpsells.schemas'
import {
  isGenerationRequestInterrupted,
  markUpsellGenerated,
  markUpsellGenerationFailed,
  markUpsellGenerationRequested,
  markUpsellGenerationStarted,
  useCartUpsellRecords,
} from '../../cartUpsellsStorage'
import { hasCartUpsellTiles } from '../../cartUpsellTiles'
import type {
  AiWallSource,
  AiWallUpsell,
  AiWallUpsellId,
} from '../aiWall.types'
import {
  trackAiWallGenerationCompleted,
  trackAiWallGenerationFailed,
  trackAiWallGenerationStarted,
  trackAiWallSubmitFailed,
} from '../aiWallAnalytics'
import {
  getAiWallPollErrorCode,
  getAiWallSubmitErrorCode,
  isTransientAiWallPollError,
  isTransientAiWallRequestError,
} from '../aiWallGeneration'

type AiWallJobResult = Awaited<ReturnType<typeof pollJobStatusUntilDone>>

type AiWallProducerProps<Id extends AiWallUpsellId, Candidate> = {
  upsell: AiWallUpsell<Id>
  source: AiWallSource<Id, Candidate>
}

export function AiWallProducer<Id extends AiWallUpsellId, Candidate>({
  upsell,
  source,
}: AiWallProducerProps<Id, Candidate>) {
  const records = useCartUpsellRecords()
  const record = records[upsell.id]
  // An interrupted request never got its jobId, so it is asked again rather than lost for the cart.
  if (!record || isGenerationRequestInterrupted(record)) {
    return <AiWallCandidateWatcher upsell={upsell} source={source} />
  }
  if (record.status === 'generating') {
    return (
      <AiWallJobTask
        key={record.jobId}
        upsell={upsell}
        source={source}
        generation={record}
      />
    )
  }
  return null
}

function AiWallCandidateWatcher<Id extends AiWallUpsellId, Candidate>({
  upsell,
  source,
}: AiWallProducerProps<Id, Candidate>) {
  const cartStore = useCartStore()
  const tilesStore = useTilesStore()
  const photoBankStore = usePhotoBankStore()
  const photoBooksStore = usePhotoBooksStore()
  const candidate = useMobxSelector(() => {
    const isCartOpenToUpsell =
      cartStore.cartItems.length > 0 &&
      !hasCartUpsellTiles(tilesStore.tiles, upsell.id)
    if (!isCartOpenToUpsell) return null
    // The photo bank outlives the orders placed from it, so only the bag's own photos may source the wall.
    const bagPhotoUids = new Set([
      ...tilesStore.getTilePhotosUids(),
      ...photoBooksStore.books.flatMap(({ cover, pages }) =>
        getBookPhotosUids(cover, pages)
      ),
    ])
    return source.findCandidate(
      photoBankStore.photos.filter(({ uid }) => bagPhotoUids.has(uid))
    )
  }, [cartStore, tilesStore, photoBankStore, photoBooksStore, upsell, source])

  if (candidate === null) return null

  return (
    <AiWallGenerationStarter
      key={source
        .getSourcePhotos(candidate)
        .map(({ uid }) => uid)
        .join()}
      upsell={upsell}
      source={source}
      candidate={candidate}
    />
  )
}

function AiWallGenerationStarter<Id extends AiWallUpsellId, Candidate>({
  upsell,
  source,
  candidate,
}: AiWallProducerProps<Id, Candidate> & { candidate: Candidate }) {
  const experimentManager = useExperimentManager()
  // The exposure point: only a settled candidate in a non-empty cart reaches it, in both variants alike.
  const isGenerationEnabled = experimentManager.isEnabled(upsell.experimentFlag)

  useEffect(() => {
    source.trackCandidateFound(candidate)
    if (!isGenerationEnabled) return
    const sourcePhotos = source.getSourcePhotos(candidate)
    const generationFields = source.getGenerationFields(candidate)
    const sourceAnalyticsProps =
      source.getSourceAnalyticsProps(generationFields)
    // Unmounts this starter, so photos changing while the POST is in flight can't send a second one.
    markUpsellGenerationRequested(upsell.id)
    // Not tied to this component: it unmounts on the write above, and the jobId must still be persisted.
    generateAIImage({
      templateSlug: upsell.templateSlug,
      variantIndexes: upsell.variantIndexes,
      imageUrls: sourcePhotos.map(({ originalUrl }) => originalUrl),
      imageDimensions: sourcePhotos.map(({ width, height }) => [width, height]),
      amplitudeDeviceId: getAmplitudeDeviceId(),
      useAiWorkflows: true,
      productFlow: upsell.productFlow,
    }).then(
      ({ jobId }) => {
        markUpsellGenerationStarted(upsell.id, generationFields, jobId)
        trackAiWallGenerationStarted(upsell, sourceAnalyticsProps, jobId)
      },
      (error: unknown) => {
        const errorCode = getAiWallSubmitErrorCode(error)
        // Left pending, so the next page load reads it as interrupted and asks again.
        if (!isTransientAiWallRequestError(error)) {
          markUpsellGenerationFailed(upsell.id, errorCode)
        }
        trackAiWallSubmitFailed(upsell, sourceAnalyticsProps, errorCode)
      }
    )
  }, [upsell, source, candidate, isGenerationEnabled])

  return null
}

function AiWallJobTask<Id extends AiWallUpsellId, Candidate>({
  upsell,
  source,
  generation,
}: AiWallProducerProps<Id, Candidate> & {
  generation: CartUpsellGeneration<AiWallUpsellId>
}) {
  const sourceAnalyticsProps = source.getSourceAnalyticsProps(generation)
  const failGeneration = (errorCode: string) => {
    markUpsellGenerationFailed(upsell.id, errorCode)
    trackAiWallGenerationFailed(
      upsell,
      sourceAnalyticsProps,
      generation,
      errorCode
    )
  }

  const onJobDone = useEffectEvent(
    ({ urls, width, height }: AiWallJobResult) => {
      if (urls.some((result) => result.status === 'error')) {
        failGeneration('partial_result')
        return
      }
      const offerParseResult = source.offerSchema.safeParse({
        ...omit(generation, ['status', 'startedAt']),
        results: urls,
        width,
        height,
      })
      // e.g. the template returned a scene count other than the upsell's; storing it would read back as unreadable.
      if (!offerParseResult.success) {
        failGeneration('invalid_result')
        return
      }
      markUpsellGenerated(upsell.id, offerParseResult.data)
      trackAiWallGenerationCompleted(upsell, sourceAnalyticsProps, generation)
    }
  )

  const onJobFailed = useEffectEvent((error: unknown) => {
    if (isTransientAiWallPollError(error)) return
    failGeneration(getAiWallPollErrorCode(error))
  })

  useEffect(() => {
    const controller = new AbortController()
    // The poll checks the signal only between GETs, so a GET in flight at unmount can still resolve.
    pollJobStatusUntilDone(generation.jobId, controller.signal).then(
      (jobResult) => {
        if (!controller.signal.aborted) onJobDone(jobResult)
      },
      (error: unknown) => {
        if (!controller.signal.aborted) onJobFailed(error)
      }
    )
    return () => controller.abort()
  }, [generation.jobId])

  return null
}
